import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  User,
  Phone,
  Mail,
  Cake,
  Plus,
  CheckCircle,
  AlertCircle,
  Users,
  Clock,
} from "lucide-react";
import { toast } from "react-toastify";

// Today's date as "YYYY-MM-DD", used to cap the birthday input so a future date can't be picked.
const TODAY_ISO_DATE = new Date().toISOString().slice(0, 10);

const SingleContactForm = ({ groupName, onAddContact, onRefresh }) => {
  const [formData, setFormData] = useState({
    phone_number: "",
    first_name: "",
    last_name: "",
    email: "",
    birthday: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [recentContacts, setRecentContacts] = useState([]);

  const validateForm = () => {
    const newErrors = {};

    // Phone number validation
    if (!formData.phone_number.trim()) {
      newErrors.phone_number = "Phone number is required";
    } else {
      // Remove spaces and validate
      const cleanPhone = formData.phone_number
        .replace(/\s/g, "")
        .replace(/\D/g, "");
      if (cleanPhone.length !== 12 || !cleanPhone.startsWith("233")) {
        newErrors.phone_number =
          "Phone number must be 12 digits starting with 233";
      }
    }

    // At least one name field should be filled
    if (!formData.first_name.trim() && !formData.last_name.trim()) {
      newErrors.first_name = "At least first name or last name is required";
    }

    // Email validation (optional but if provided, should be valid)
    if (
      formData.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      newErrors.email = "Please enter a valid email address";
    }

    // Birthday validation (optional but if provided, can't be in the future)
    if (formData.birthday && formData.birthday > TODAY_ISO_DATE) {
      newErrors.birthday = "Birthday cannot be in the future";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      // Clean phone number
      const cleanPhone = formData.phone_number
        .replace(/\s/g, "")
        .replace(/\D/g, "");

      const contactData = {
        phone_number: cleanPhone,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim() || null,
        birthday: formData.birthday || null,
      };

      await onAddContact(contactData);

      // Add to recent contacts
      setRecentContacts((prev) => [
        {
          ...contactData,
          id: Date.now(),
          added_at: new Date().toISOString(),
        },
        ...prev.slice(0, 4),
      ]);

      // Reset form
      setFormData({
        phone_number: "",
        first_name: "",
        last_name: "",
        email: "",
        birthday: "",
      });
      setErrors({});
    } catch (error) {
      toast.error("Failed to add contact");
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const formatPhoneNumber = (value, cursorPosition) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, "");

    // Limit to 12 digits
    const limited = cleaned.slice(0, 12);

    // Format the number
    let formatted = "";
    if (limited.length <= 3) {
      formatted = limited;
    } else if (limited.length <= 6) {
      formatted = `${limited.slice(0, 3)} ${limited.slice(3)}`;
    } else if (limited.length <= 9) {
      formatted = `${limited.slice(0, 3)} ${limited.slice(
        3,
        6
      )} ${limited.slice(6)}`;
    } else {
      formatted = `${limited.slice(0, 3)} ${limited.slice(
        3,
        6
      )} ${limited.slice(6, 9)} ${limited.slice(9)}`;
    }

    return formatted;
  };

  const handlePhoneNumberChange = (e) => {
    const input = e.target;
    const value = input.value;
    const cursorPosition = input.selectionStart;

    // Count how many spaces were before the cursor in the original value
    const spacesBeforeCursor = value.slice(0, cursorPosition).split(" ").length - 1;

    // Format the value
    const formattedValue = formatPhoneNumber(value);

    // Update the form data
    handleInputChange("phone_number", formattedValue);

    // Restore cursor position after React re-renders
    setTimeout(() => {
      const digits = value.replace(/\D/g, "").length;
      let newCursorPosition = 0;

      // Calculate new cursor position based on digit count
      if (digits <= 3) {
        newCursorPosition = digits;
      } else if (digits <= 6) {
        newCursorPosition = digits + 1; // +1 for the space
      } else if (digits <= 9) {
        newCursorPosition = digits + 2; // +2 for two spaces
      } else {
        newCursorPosition = digits + 3; // +3 for three spaces
      }

      input.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
      >
        <div className="flex gap-3 items-center mb-6">
          <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-lg">
            <User className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Add Single Contact
            </h2>
            <p className="text-gray-600">Add a new contact to {groupName}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Phone Number */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Phone Number *
            </label>
            <div className="relative">
              <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                <Phone className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="tel"
                value={formData.phone_number}
                onChange={handlePhoneNumberChange}
                placeholder="233 XXX XXX XXXX"
                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                  errors.phone_number
                    ? "border-red-300 bg-red-50"
                    : "border-gray-300"
                }`}
                disabled={submitting}
                maxLength={17}
              />
            </div>
            {errors.phone_number && (
              <p className="flex gap-1 items-center mt-1 text-sm text-red-600">
                <AlertCircle className="w-4 h-4" />
                {errors.phone_number}
              </p>
            )}
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                First Name
              </label>
              <div className="relative">
                <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                  <User className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) =>
                    handleInputChange("first_name", e.target.value)
                  }
                  placeholder="John"
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.first_name
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300"
                  }`}
                  disabled={submitting}
                />
              </div>
              {errors.first_name && (
                <p className="flex gap-1 items-center mt-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  {errors.first_name}
                </p>
              )}
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Last Name
              </label>
              <div className="relative">
                <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                  <User className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) =>
                    handleInputChange("last_name", e.target.value)
                  }
                  placeholder="Doe"
                  className="py-3 pr-4 pl-10 w-full rounded-xl border border-gray-300 transition-all focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={submitting}
                />
              </div>
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Email (Optional)
            </label>
            <div className="relative">
              <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                <Mail className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="john.doe@example.com"
                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                  errors.email ? "border-red-300 bg-red-50" : "border-gray-300"
                }`}
                disabled={submitting}
              />
            </div>
            {errors.email && (
              <p className="flex gap-1 items-center mt-1 text-sm text-red-600">
                <AlertCircle className="w-4 h-4" />
                {errors.email}
              </p>
            )}
          </div>

          {/* Birthday */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Birthday (Optional)
            </label>
            <div className="relative">
              <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                <Cake className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="date"
                value={formData.birthday}
                onChange={(event) => handleInputChange("birthday", event.target.value)}
                max={TODAY_ISO_DATE}
                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                  errors.birthday ? "border-red-300 bg-red-50" : "border-gray-300"
                }`}
                disabled={submitting}
              />
            </div>
            {errors.birthday && (
              <p className="flex gap-1 items-center mt-1 text-sm text-red-600">
                <AlertCircle className="w-4 h-4" />
                {errors.birthday}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <motion.button
            type="submit"
            disabled={submitting}
            className="flex gap-2 justify-center items-center px-6 py-3 w-full font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl transition-all hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {submitting ? (
              <>
                <div className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent" />
                <span>Adding Contact...</span>
              </>
            ) : (
              <>
                <Plus className="w-5 h-5" />
                <span>Add Contact</span>
              </>
            )}
          </motion.button>
        </form>
      </motion.div>

      {/* Recent Contacts */}
      {recentContacts.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
        >
          <div className="flex gap-3 items-center mb-4">
            <div className="flex justify-center items-center w-8 h-8 bg-red-100 rounded-lg">
              <CheckCircle className="w-4 h-4 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Recently Added
            </h3>
          </div>

          <div className="space-y-3">
            {recentContacts.map((contact) => (
              <motion.div
                key={contact.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex gap-3 items-center p-3 bg-red-50 rounded-lg border border-red-200"
              >
                <div className="flex justify-center items-center w-8 h-8 bg-red-100 rounded-full">
                  <Users className="w-4 h-4 text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-red-800">
                    {contact.first_name} {contact.last_name}
                  </p>
                  <p className="text-sm text-red-600">{contact.phone_number}</p>
                  {contact.email && (
                    <p className="text-xs text-red-500">{contact.email}</p>
                  )}
                </div>
                <div className="flex gap-1 items-center text-xs text-red-600">
                  <Clock className="w-3 h-3" />
                  <span>Just now</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Tips Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 bg-blue-50 rounded-2xl border border-blue-200"
      >
        <div className="flex gap-3 items-start">
          <div className="flex flex-shrink-0 justify-center items-center w-8 h-8 bg-blue-100 rounded-lg">
            <AlertCircle className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <h3 className="mb-2 font-semibold text-blue-800">
              Tips for Adding Contacts
            </h3>
            <ul className="space-y-1 text-sm text-blue-700">
              <li>• Phone numbers must be 12 digits starting with 233</li>
              <li>• At least first name or last name is required</li>
              <li>
                • Email is optional but recommended for better contact
                management
              </li>
              <li>• Duplicate phone numbers will be automatically handled</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SingleContactForm;
